import Phaser from "phaser";

/**
 * Draws the attack field (cone or line) from the player position.
 * @param scene Phaser.Scene
 * @param x Player X position
 * @param y Player Y position
 * @param direction {x, y} normalized aim direction
 * @param range Attack range (pixels)
 * @param accuracy Weapon accuracy (0-100)
 * @param color Color of the field (default: 0xff0000)
 * @param alpha Transparency (default: 0.25)
 * @returns Phaser.GameObjects.Graphics
 */
export function drawAttackField(
  scene: Phaser.Scene,
  x: number,
  y: number,
  direction: { x: number; y: number },
  range: number,
  accuracy: number,
  color: number = 0xff0000,
  alpha: number = 0.25,
  spreadAngle?: number, // in radians, optional
): Phaser.GameObjects.Graphics {
  // Remove previous field if needed (caller responsibility)
  const graphics = scene.add.graphics();
  graphics.setDepth(8); // Render FOA under player (player uses depth 10)
  graphics.setScrollFactor(1); // World coordinates
  graphics.clear();

  // Make sure UI camera ignores this graphics if it exists (game world element)
  if ((scene as any).uiCamera) {
    (scene as any).uiCamera.ignore(graphics);
  }

  // Calculate angle and cone width
  const aimAngle = Math.atan2(direction.y, direction.x);
  // Correct spread calculation: 100 accuracy = 0°, 0 accuracy = 90°, 50 accuracy = 45°
  const coneHalfAngle =
    spreadAngle !== undefined
      ? spreadAngle / 2
      : ((90 - accuracy * 0.9) * Math.PI) / 180 / 2; // 0 to 45deg

  // Threshold for where fade begins
  const fadeThreshold = 200;

  // Helper function to draw a full cone with fading from origin to fadeThreshold
  const drawConeWithGradient = (maxRange: number) => {
    const steps = 32;
    const drawRange = Math.min(maxRange, fadeThreshold);
    const layerSteps = 16; // Number of layers for smooth fade

    // Draw cone in layers with decreasing opacity
    for (let layer = 0; layer < layerSteps; layer++) {
      const innerRadius = (drawRange / layerSteps) * layer;
      const outerRadius = (drawRange / layerSteps) * (layer + 1);

      // Calculate opacity: starts at 0.4, fades to 0 at fadeThreshold
      const layerAlpha = alpha * 0.4 * (1 - outerRadius / fadeThreshold);

      graphics.fillStyle(color, layerAlpha);

      const points: Phaser.Types.Math.Vector2Like[] = [];

      // Inner arc
      for (let i = 0; i <= steps; i++) {
        const angle =
          aimAngle - coneHalfAngle + (2 * coneHalfAngle * i) / steps;
        const px = x + Math.cos(angle) * innerRadius;
        const py = y + Math.sin(angle) * innerRadius;
        points.push({ x: px, y: py });
      }

      // Outer arc (reversed)
      for (let i = steps; i >= 0; i--) {
        const angle =
          aimAngle - coneHalfAngle + (2 * coneHalfAngle * i) / steps;
        const px = x + Math.cos(angle) * outerRadius;
        const py = y + Math.sin(angle) * outerRadius;
        points.push({ x: px, y: py });
      }

      graphics.beginPath();
      graphics.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i++) {
        graphics.lineTo(points[i].x, points[i].y);
      }
      graphics.closePath();
      graphics.fillPath();
    }
  };

  if (spreadAngle !== undefined) {
    // Draw cone for unarmed/melee with gradient
    drawConeWithGradient(range);

    // Draw outline for cone with fade
    const outlineSteps = 32;
    const drawRange = Math.min(range, fadeThreshold);
    const layerSteps = 16;

    // Draw outline in layers
    for (let layer = 0; layer < layerSteps; layer++) {
      const currentRadius = (drawRange / layerSteps) * (layer + 1);
      const outlineAlpha = 0.1 * (1 - currentRadius / fadeThreshold);

      graphics.lineStyle(2, color, outlineAlpha);
      const outlinePoints: Phaser.Types.Math.Vector2Like[] = [];
      outlinePoints.push({ x, y });
      for (let i = 0; i <= outlineSteps; i++) {
        const angle =
          aimAngle - coneHalfAngle + (2 * coneHalfAngle * i) / outlineSteps;
        const px = x + Math.cos(angle) * currentRadius;
        const py = y + Math.sin(angle) * currentRadius;
        outlinePoints.push({ x: px, y: py });
      }
      graphics.beginPath();
      graphics.moveTo(outlinePoints[0].x, outlinePoints[0].y);
      for (let i = 1; i < outlinePoints.length; i++) {
        graphics.lineTo(outlinePoints[i].x, outlinePoints[i].y);
      }
      graphics.closePath();
      graphics.strokePath();
    }

    // Draw a line from player to mouse pointer for feedback with fade
    const lineLayerSteps = 16;
    for (let layer = 0; layer < lineLayerSteps; layer++) {
      const startDist = (drawRange / lineLayerSteps) * layer;
      const endDist = (drawRange / lineLayerSteps) * (layer + 1);
      const lineAlpha = 0.1 * (1 - endDist / fadeThreshold);

      graphics.lineStyle(1, 0xffffff, lineAlpha);
      graphics.beginPath();
      graphics.moveTo(x + direction.x * startDist, y + direction.y * startDist);
      graphics.lineTo(x + direction.x * endDist, y + direction.y * endDist);
      graphics.strokePath();
    }
  } else if (accuracy >= 99) {
    // Draw a line for perfect accuracy
    graphics.lineStyle(4, color, alpha);
    graphics.beginPath();
    graphics.moveTo(x, y);
    graphics.lineTo(x + direction.x * range, y + direction.y * range);
    graphics.strokePath();

    // Draw a small circle at the end for visibility
    graphics.lineStyle(2, color, 0.2);
    graphics.strokeCircle(x + direction.x * range, y + direction.y * range, 8);
  } else {
    // Draw a cone sector for normal accuracy
    drawConeWithGradient(range);

    // Draw outline for cone with fade
    const outlineSteps = 32;
    const drawRange = Math.min(range, fadeThreshold);
    const layerSteps = 16;

    // Draw outline in layers
    for (let layer = 0; layer < layerSteps; layer++) {
      const currentRadius = (drawRange / layerSteps) * (layer + 1);
      const outlineAlpha = 0.1 * (1 - currentRadius / fadeThreshold);

      graphics.lineStyle(2, color, outlineAlpha);
      const outlinePoints: Phaser.Types.Math.Vector2Like[] = [];
      outlinePoints.push({ x, y });
      for (let i = 0; i <= outlineSteps; i++) {
        const angle =
          aimAngle - coneHalfAngle + (2 * coneHalfAngle * i) / outlineSteps;
        const px = x + Math.cos(angle) * currentRadius;
        const py = y + Math.sin(angle) * currentRadius;
        outlinePoints.push({ x: px, y: py });
      }
      graphics.beginPath();
      graphics.moveTo(outlinePoints[0].x, outlinePoints[0].y);
      for (let i = 1; i < outlinePoints.length; i++) {
        graphics.lineTo(outlinePoints[i].x, outlinePoints[i].y);
      }
      graphics.closePath();
      graphics.strokePath();
    }

    // Draw a line from player to mouse pointer for feedback with fade
    const lineLayerSteps = 16;
    for (let layer = 0; layer < lineLayerSteps; layer++) {
      const startDist = (drawRange / lineLayerSteps) * layer;
      const endDist = (drawRange / lineLayerSteps) * (layer + 1);
      const lineAlpha = 0.1 * (1 - endDist / fadeThreshold);

      graphics.lineStyle(1, 0xffffff, lineAlpha);
      graphics.beginPath();
      graphics.moveTo(x + direction.x * startDist, y + direction.y * startDist);
      graphics.lineTo(x + direction.x * endDist, y + direction.y * endDist);
      graphics.strokePath();
    }
  }
  return graphics;
}
